import uploadClient from "./clients/uploadClient";
import presignedClient from "./clients/presignedClient";

/**
 * Upload one media file into the evidence locker.
 *
 * This is where video and audio enter the system. The incidents API no longer
 * accepts either: it hosts no media bytes and produces no transcripts, so a clip
 * uploaded to it could never be transcribed, cited by timestamp, or shown with a
 * storyboard. Media goes to the evidence service, and the incident references it.
 *
 * Deliberately NOT a hook. Its callers need it inside very different machinery —
 * a staged-upload queue with per-row cancellation, a page-level job list, and
 * the evidence picker's own upload lane — and every piece of React state here
 * would have to be threaded through all of them. What they actually share is a
 * four-step protocol with three failure modes, which is all this module is.
 *
 * The protocol:
 *   1. POST /uploadEvidence            presign; the evidence row is created NOW,
 *                                      at FETCHING, before a single byte moves.
 *   2. PUT to S3                       one request, or N parts above ~64 MiB.
 *   3. PUT /uploadEvidence  success    completes the multipart upload and
 *                                      releases the row into the pipeline.
 *   4. PUT /uploadEvidence  failure    DELETES the row and aborts the multipart
 *                                      upload. Step 1 already created the row,
 *                                      so skipping this on failure leaks it.
 */

/**
 * Extension → Content-Type, mirroring bodycam's `CONTENT_TYPE_BY_EXTENSION`.
 *
 * >>> A CROSS-REPO CONTRACT, and the PUT fails without it. <<< The presigned URL
 * is signed against the Content-Type the server derived from the FILENAME, so the
 * browser's own `file.type` is the wrong value to send whenever the two disagree
 * — and they disagree routinely: an `.m4a` is `audio/x-m4a` in Chrome and
 * `audio/mp4` to the signer, an `.ogg` may arrive as `audio/vorbis`, and a file
 * dragged from some sources has no type at all. Sending `file.type` yields
 * `SignatureDoesNotMatch`, which surfaces as an opaque 403 on an upload that
 * looked fine.
 */
const CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  flac: "audio/flac",
  ogg: "audio/ogg",
  opus: "audio/ogg",
  aac: "audio/aac",
  mp2: "audio/mpeg",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mpg: "video/mpeg",
  m4v: "video/x-m4v",
};

/**
 * Media the evidence service can ingest. NARROWER than what the file picker used
 * to accept: `.mkv`, `.wmv` and `.wma` are absent because the transcription
 * engine cannot read them, and an upload of one is rejected at presign. Checked
 * here so that rejection is a clear message at selection time rather than a 400
 * after the user has waited for a large file.
 */
export const SUPPORTED_EVIDENCE_EXTENSIONS: ReadonlySet<string> = new Set(
  Object.keys(CONTENT_TYPE_BY_EXTENSION),
);

/**
 * `accept` for a media-only file picker, derived from the map above so the hint
 * and the check can never drift. Extensions rather than MIME types: the browser
 * types `.m4a`, `.mov` and friends inconsistently, and a picker that filters on
 * `audio/mp4` hides files it would happily have uploaded.
 */
export const EVIDENCE_ACCEPT_ATTR: string = Object.keys(
  CONTENT_TYPE_BY_EXTENSION,
)
  .map((ext) => `.${ext}`)
  .join(",");

export function fileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  if (i < 0 || i === name.length - 1) return "";
  return name.slice(i + 1).toLowerCase();
}

export function isSupportedEvidenceFile(file: File): boolean {
  return SUPPORTED_EVIDENCE_EXTENSIONS.has(fileExtension(file.name));
}

/** The Content-Type the presigned URL was signed with. See the map above. */
export function evidenceContentType(file: File): string {
  return (
    CONTENT_TYPE_BY_EXTENSION[fileExtension(file.name)] ||
    file.type ||
    "application/octet-stream"
  );
}

/**
 * Duration in whole seconds, which the presign request requires to be > 0.
 *
 * Uses an `<audio>` element for audio and `<video>` for video: a video element
 * will happily load many audio files, but not all of them, and a rejected
 * `onerror` here fails the upload before it starts.
 */
export function getMediaDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const isAudio = evidenceContentType(file).startsWith("audio/");
    const element = document.createElement(isAudio ? "audio" : "video");
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      element.remove();
    };

    element.preload = "metadata";
    element.onloadedmetadata = () => {
      const seconds = Math.floor(element.duration);
      cleanup();
      // A stream with no seekable duration reports Infinity or NaN. Both
      // marshal to a value the presign schema rejects, so fall back to 1: the
      // field is metadata, and refusing the upload over it helps nobody.
      resolve(Number.isFinite(seconds) && seconds > 0 ? seconds : 1);
    };
    element.onerror = () => {
      cleanup();
      reject(new Error(`Could not read media metadata for ${file.name}`));
    };
    element.src = objectUrl;
  });
}

interface PresignEntry {
  evidenceID: string;
  presignedUrl?: string;
  presignedUrls?: string[];
  uploadId?: string;
}

export interface UploadEvidenceParams {
  file: File;
  /** Seconds. Resolved by `getMediaDuration` when the caller has not already. */
  duration?: number;
  /** ISO 8601. Omitted values are normalised server-side. */
  recordedAt?: string;
  /**
   * Ask the evidence service to transcribe the clip the moment it becomes
   * playable, instead of waiting to be asked. Incidents needs this: a transcript
   * is what its analysis reads, and READY arrives minutes later inside the
   * compression pipeline with nothing to notify the client.
   */
  transcribeOnReady?: boolean;
  signal?: AbortSignal;
  /** 0-100. */
  onProgress?: (percent: number) => void;
}

/** Parts above this size are uploaded singly; the server decides, we follow. */
const MULTIPART_CONCURRENCY = 4;

export async function uploadEvidenceFile(
  params: UploadEvidenceParams,
): Promise<string> {
  const { file, signal, onProgress } = params;
  const contentType = evidenceContentType(file);
  const duration = params.duration ?? (await getMediaDuration(file));

  const { data } = await uploadClient.post(
    "/uploadEvidence",
    {
      videos: [
        {
          name: file.name,
          size: file.size,
          duration,
          ...(params.recordedAt ? { recordedAt: params.recordedAt } : {}),
        },
      ],
      ...(params.transcribeOnReady ? { transcribeOnReady: true } : {}),
    },
    { signal },
  );

  const entry: PresignEntry | undefined = data?.presignedUrls?.[file.name];
  if (!entry?.evidenceID) {
    throw new Error(`The evidence service returned no upload URL for ${file.name}`);
  }
  const evidenceId = entry.evidenceID;

  try {
    if (entry.presignedUrl) {
      await putWholeFile(entry.presignedUrl, file, contentType, signal, onProgress);
      await confirm(evidenceId, true);
    } else if (entry.presignedUrls?.length) {
      const parts = await putParts(
        entry.presignedUrls,
        file,
        contentType,
        signal,
        onProgress,
      );
      await confirm(evidenceId, true, entry.uploadId, parts);
    } else {
      throw new Error(`The evidence service returned no upload URL for ${file.name}`);
    }
  } catch (error) {
    // The row exists from step 1, so every failure past that point — including a
    // user cancellation — has to tell the server, or it lingers as a FETCHING
    // row with no bytes until a reaper collects it. Best-effort: a failed abort
    // must not replace the real error with its own.
    await confirm(evidenceId, false, entry.uploadId).catch(() => undefined);
    throw error;
  }

  return evidenceId;
}

function confirm(
  evidenceID: string,
  uploadSuccessful: boolean,
  uploadId?: string,
  parts?: { partNumber: number; eTag: string }[],
): Promise<unknown> {
  return uploadClient.put("/uploadEvidence", {
    evidenceID,
    uploadSuccessful,
    ...(uploadId ? { uploadId } : {}),
    ...(parts ? { parts } : {}),
  });
}

async function putWholeFile(
  url: string,
  file: File,
  contentType: string,
  signal: AbortSignal | undefined,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await presignedClient.put(url, file, {
    signal,
    headers: {
      "Content-Type": contentType,
      // The bucket is SSE-KMS and the presign is signed with this header; the
      // PUT is rejected without it.
      "x-amz-server-side-encryption": "aws:kms",
    },
    onUploadProgress: (e) => {
      if (e.total) onProgress?.((e.loaded / e.total) * 100);
    },
  });
}

async function putParts(
  urls: string[],
  file: File,
  contentType: string,
  signal: AbortSignal | undefined,
  onProgress?: (percent: number) => void,
): Promise<{ partNumber: number; eTag: string }[]> {
  const chunkSize = Math.ceil(file.size / urls.length);
  const loadedPerPart = new Array<number>(urls.length).fill(0);
  const eTags = new Array<string | undefined>(urls.length).fill(undefined);

  // Aggregate progress across parts, so a 12-part upload still reports one
  // monotonic percentage rather than each part restarting at zero.
  const reportProgress = () => {
    const loaded = loadedPerPart.reduce((a, b) => a + b, 0);
    if (file.size) onProgress?.((loaded / file.size) * 100);
  };

  let nextPart = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = nextPart;
      if (index >= urls.length) return;
      nextPart += 1;

      const start = index * chunkSize;
      const response = await presignedClient.put(
        urls[index],
        file.slice(start, Math.min(start + chunkSize, file.size)),
        {
          signal,
          headers: { "Content-Type": contentType },
          onUploadProgress: (e) => {
            loadedPerPart[index] = e.loaded || 0;
            reportProgress();
          },
        },
      );
      // S3 returns the part's ETag in a header; CompleteMultipartUpload needs
      // every one of them, so a missing header has to fail the upload rather
      // than complete it with a hole.
      const eTag = response?.headers?.etag ?? response?.headers?.ETag;
      if (!eTag) throw new Error(`Missing ETag for part ${index + 1}`);
      eTags[index] = eTag as string;
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(MULTIPART_CONCURRENCY, urls.length) }, worker),
  );

  return eTags.map((eTag, i) => {
    if (!eTag) throw new Error(`Missing ETag for part ${i + 1}`);
    return { partNumber: i + 1, eTag };
  });
}
